import { useEffect, useState, Fragment } from "react";

// UI IMPORT
import {
  Button,
  ControlledDatePicker,
  Table,
  TableRow,
} from "../../ui";

// PROJECT IMPORT
import { dateFormatter, seperator } from "../../helperFunctions";
import { FILTER } from "../../constant";
import { Card } from "../common";

// THIRD - PARTY IMPORT
import { useForm } from "react-hook-form";
import { useBalanceData } from "./balanceSlice";
import { useGetBalanceSummaryMutation } from "./balanceApi";

const getDefaultDates = () => {
  const date = new Date();
  const startDate = new Date(date.getFullYear(), date.getMonth(), 1);
  return {
    date: [startDate, new Date()],
  };
};

const initialValues = {
  ...getDefaultDates(),
};

const Balance = () => {
  const balanceData = useBalanceData();
  const [filter, setFilter] = useState(FILTER);
  const [getBalanceSummary, { isLoading }] = useGetBalanceSummaryMutation();

  const columns = [
    {
      title: "Merchant ID",
      name: "merchant_id",
    },
    {
      title: "Merchant Name",
      name: "merchant_name",
    },
    {
      title: "Available Balance",
      name: "available_balance",
      Cell: (item: any) => <span>{seperator(item?.available_balance)}</span>,
    },
    {
      title: "Unsettled Balance",
      name: "unsettle_balance",
      Cell: (item: any) => <span>{seperator(item?.unsettle_balance)}</span>,
    },
    {
      title: "Deposit",
      name: "deposit",
      Cell: (item: any) => <span>{seperator(item?.deposit)}</span>,
    },
    {
      title: "Withdrawal",
      name: "withdrawal",
      Cell: (item: any) => <span>{seperator(item?.withdrawal)}</span>,
    },
  ];

  const { handleSubmit, reset, getValues, control } = useForm({
    defaultValues: {
      ...initialValues,
    },
  });

  const onGetData = async () => {
    const values = getValues();
    const payload = {
      filterData: {
        startDate: dateFormatter(values?.date?.[0] || getDefaultDates().date[0], "start"),
        endDate: dateFormatter(values?.date?.[1] || getDefaultDates().date[1], "end"),
      },
    };
    getBalanceSummary(payload);
  };

  useEffect(() => {
    onGetData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filter]);

  const onSubmit = async () => {
    setFilter({ ...FILTER, isFilter: true });
  };

  const list = balanceData?.data || [];

  const totalAvailable = list.reduce((acc: number, item: any) => acc + (Number(item?.available_balance) || 0), 0);
  const totalUnsettled = list.reduce((acc: number, item: any) => acc + (Number(item?.unsettle_balance) || 0), 0);
  const totalDeposit = list.reduce((acc: number, item: any) => acc + (Number(item?.deposit) || 0), 0);
  const totalWithdrawal = list.reduce((acc: number, item: any) => acc + (Number(item?.withdrawal) || 0), 0);

  const summaryData = [
    {
      imgSrc: "DashboardActiveBalance" as const,
      key: "available_balance",
      text: "Total Available Balance",
      value: totalAvailable,
    },
    {
      imgSrc: "DashboardUnsettled" as const,
      key: "unsettle_balance",
      text: "Total Unsettled Balance",
      value: totalUnsettled,
    },
    {
      imgSrc: "DashboardDeposite" as const,
      key: "deposit",
      text: "Total Deposit",
      value: totalDeposit,
    },
    {
      imgSrc: "DashboardWithdrawal" as const,
      key: "withdrawal",
      text: "Total Withdrawal",
      value: totalWithdrawal,
    },
  ];

  return (
    <>
      <div className="mt-[16px]">
        <div className="mt-2">
          {/* Summary Cards */}
          <div className="grid grid-cols-4 sm:grid-cols-2 md:grid-cols-2 gap-4 mainbox">
            {summaryData?.map((item: any, index: number) => {
              return (
                <Fragment key={index}>
                  <Card
                    length={summaryData.length}
                    index={index}
                    value={item.value}
                    text={item?.text}
                    icon={item?.imgSrc}
                    rupee={true}
                  />
                </Fragment>
              );
            })}
          </div>

          {/* Filter Form */}
          <form
            className="flex flex-wrap gap-2 items-start justify-end px-1 mt-2"
            onSubmit={handleSubmit(onSubmit)}
          >
            <ControlledDatePicker
              name="date"
              label="Date"
              placeholder="Date"
              selectsRange
              maxDate={new Date()}
              control={control}
            />
            <Button type="submit" className="mt-[22px] !h-9 !text-sm">
              Apply
            </Button>
            <Button
              variant="Transaparent"
              className="mt-[22px]  !text-sm !h-9"
              onClick={() => {
                reset();
                setFilter({ ...FILTER });
              }}
            >
              Clear
            </Button>
          </form>

          {/* Table */}
          <div className="App mt-3 tableborder">
            <Table
              isExpendable={false}
              columns={columns}
              isLoading={isLoading}
              data={list}
            >
              {list?.map((item: any, index: number) => {
                return (
                  <Fragment key={index}>
                    <TableRow
                      columns={columns}
                      item={item}
                      isExpendable={false}
                    />
                  </Fragment>
                );
              })}
            </Table>
          </div>
        </div>
      </div>
    </>
  );
};

export default Balance;
